/**
 * IndicatorRailView - Pure presentation component for the indicator tile row
 *
 * On desktop: displays 5 key indicator flexible-width tiles in a left-aligned
 *   row that wraps to additional rows on narrow desktop widths instead of
 *   scrolling laterally (SE-4616 — `overflow-x-auto` produced an unscrollable
 *   off-screen overflow with no affordance; `flex-wrap` + `justify-start`
 *   wraps and keeps a trailing partial row left-aligned).
 * On mobile: displays tiles in a 2x2 grid with full-width transfer value below.
 * @remarks This is a View component - contains no business logic, hooks, or state
 */

import { memo } from "react";

import { Stack } from "@/components/atoms/Stack";

import type { IndicatorRailViewProps } from "@/features/player-detail/utils/playerPageHeaderTypes";
import IndicatorTileView from "@/features/player-detail/components/molecules/IndicatorTile/IndicatorTileView";

/**
 * IndicatorRailView renders indicator tiles in a responsive layout
 * @param root0 - View props including array of indicator data
 * @param root0.indicators - Array of formatted indicator objects with type, value, color, label, and press handler
 * @param root0.isDark - Whether dark theme is active, affects tile styling
 * @param root0.isSmallScreen - Whether to use mobile 2x2 grid layout
 * @param root0.isCompact - Whether to render as compact inline chips (used in modal context)
 * @returns Layout containing indicator tiles
 */
const IndicatorRailView = ({
  indicators,
  isDark,
  isSmallScreen = false,
  isCompact = false,
}: IndicatorRailViewProps) =>
  isCompact ? (
    <Stack
      direction="horizontal"
      className="w-full flex-wrap items-stretch justify-start gap-3"
    >
      {indicators.map(indicator => (
        <IndicatorTileView
          key={indicator.type}
          label={indicator.label}
          value={indicator.value}
          valueColor={indicator.valueColor}
          onPress={indicator.onPress}
          testID={`indicator-tile-${indicator.type}`}
          isDark={isDark}
          isCompact
        />
      ))}
    </Stack>
  ) : isSmallScreen ? (
    <Stack className="w-full gap-2">
      <Stack direction="horizontal" className="w-full gap-2">
        {indicators
          .filter(i => i.type !== "transferValue")
          .slice(0, 2)
          .map(indicator => (
            <IndicatorTileView
              key={indicator.type}
              label={indicator.label}
              value={indicator.value}
              valueColor={indicator.valueColor}
              onPress={indicator.onPress}
              testID={`indicator-tile-${indicator.type}`}
              isDark={isDark}
              isSmallScreen={isSmallScreen}
              showArrow={indicator.showArrow}
            />
          ))}
      </Stack>
      <Stack direction="horizontal" className="w-full gap-2">
        {indicators
          .filter(i => i.type !== "transferValue")
          .slice(2, 4)
          .map(indicator => (
            <IndicatorTileView
              key={indicator.type}
              label={indicator.label}
              value={indicator.value}
              valueColor={indicator.valueColor}
              onPress={indicator.onPress}
              testID={`indicator-tile-${indicator.type}`}
              isDark={isDark}
              isSmallScreen={isSmallScreen}
              showArrow={indicator.showArrow}
            />
          ))}
      </Stack>
      {indicators
        .filter(i => i.type === "transferValue")
        .map(indicator => (
          <IndicatorTileView
            key={indicator.type}
            label={indicator.label}
            value={indicator.value}
            valueColor={indicator.valueColor}
            onPress={indicator.onPress}
            testID={`indicator-tile-${indicator.type}`}
            isDark={isDark}
            isSmallScreen={isSmallScreen}
            isFullWidth
            showArrow={indicator.showArrow}
          />
        ))}
    </Stack>
  ) : (
    <Stack
      direction="horizontal"
      testID="indicator-rail"
      className="w-full flex-wrap items-stretch justify-start gap-4"
    >
      {indicators.map(indicator => (
        <IndicatorTileView
          key={indicator.type}
          label={indicator.label}
          value={indicator.value}
          valueColor={indicator.valueColor}
          onPress={indicator.onPress}
          testID={`indicator-tile-${indicator.type}`}
          isDark={isDark}
          showArrow={indicator.showArrow}
        />
      ))}
    </Stack>
  );

IndicatorRailView.displayName = "IndicatorRailView";

export default memo(IndicatorRailView);
